"use client";

import { Compass } from "lucide-react";
import { useMemo, useState } from "react";

import { Card } from "@/components/ui-system";
import type { DeviceChannelDto, TelemetryAggregatePointDto, TelemetryDto } from "@/lib/api/types";
// import { cn } from "@/lib/utils/cn";
import { formatTelemetryValue, getChannelDisplayLabel } from "@/modules/telemetry/page-utils";

type WindRoseSample = {
  speed: number;
  direction: number;
};

type SectorBin = {
  count: number;
  speedTotal: number;
};

type SpeedBinMeta = {
  min: number;
  max: number;
  color: string;
};

type ActiveSegment = {
  sectorIndex: number;
  binIndex: number;
  count: number;
  totalPercent: number;
  averageSpeed: number;
  directionLabel: string;
  speedLabel: string;
};

const SECTOR_COUNT = 12;
const SPEED_BIN_COLORS = [
  "var(--viz-series-1)",
  "var(--viz-series-2)",
  "var(--viz-series-3)",
  "var(--viz-series-4)",
  "var(--viz-series-5)",
];
const CARDINAL_LABELS = [
  { label: "N", angle: -90 },
  { label: "E", angle: 0 },
  { label: "S", angle: 90 },
  { label: "W", angle: 180 },
] as const;

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeDirection(value: number): number {
  const normalized = value % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function buildWindRoseSamples(
  speedChannelKey: string,
  directionChannelKey: string,
  rawRows: TelemetryDto[],
  aggregatePoints: TelemetryAggregatePointDto[],
): WindRoseSample[] {
  const rawSamples = rawRows
    .map((row) => {
      const speed = coerceNumber(row.data?.[speedChannelKey]);
      const direction = coerceNumber(row.data?.[directionChannelKey]);
      return speed != null && direction != null ? { speed, direction: normalizeDirection(direction) } : null;
    })
    .filter((sample): sample is WindRoseSample => sample != null);

  if (rawSamples.length > 0) {
    return rawSamples;
  }

  return aggregatePoints
    .map((point) => {
      const speed = coerceNumber(point.values[speedChannelKey]);
      const direction = coerceNumber(point.values[directionChannelKey]);
      return speed != null && direction != null ? { speed, direction: normalizeDirection(direction) } : null;
    })
    .filter((sample): sample is WindRoseSample => sample != null);
}

function polarToCartesian(cx: number, cy: number, radius: number, angleDegrees: number) {
  const angleRadians = (angleDegrees * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRadians),
    y: cy + radius * Math.sin(angleRadians),
  };
}

function describeSectorPath(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startAngle: number,
  endAngle: number,
) {
  const outerStart = polarToCartesian(cx, cy, outerRadius, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
}

function buildSpeedBins(maxSpeed: number): SpeedBinMeta[] {
  const upperBound = maxSpeed > 0 ? maxSpeed : SPEED_BIN_COLORS.length;
  const binSize = upperBound / SPEED_BIN_COLORS.length;
  return SPEED_BIN_COLORS.map((color, index) => ({
    min: binSize * index,
    max: index === SPEED_BIN_COLORS.length - 1 ? upperBound : binSize * (index + 1),
    color,
  }));
}

function speedBinIndex(speed: number, bins: SpeedBinMeta[]) {
  if (bins.length === 0) {
    return 0;
  }
  for (let index = 0; index < bins.length; index += 1) {
    const bin = bins[index]!;
    if (index === bins.length - 1) {
      return speed <= bin.max ? index : bins.length - 1;
    }
    if (speed >= bin.min && speed < bin.max) {
      return index;
    }
  }
  return bins.length - 1;
}

function buildSectorMatrix(samples: WindRoseSample[]) {
  const maxSpeed = samples.reduce((highest, sample) => Math.max(highest, sample.speed), 0);
  const speedBins = buildSpeedBins(maxSpeed);
  const sectors = Array.from({ length: SECTOR_COUNT }, () =>
    Array.from({ length: speedBins.length }, () => ({ count: 0, speedTotal: 0 })),
  ) as SectorBin[][];

  samples.forEach((sample) => {
    const sectorSize = 360 / SECTOR_COUNT;
    const adjusted = normalizeDirection(sample.direction + sectorSize / 2);
    const sectorIndex = Math.floor(adjusted / sectorSize) % SECTOR_COUNT;
    const binIndex = speedBinIndex(sample.speed, speedBins);
    sectors[sectorIndex]![binIndex]!.count += 1;
    sectors[sectorIndex]![binIndex]!.speedTotal += sample.speed;
  });

  const totals = sectors.map((bins) => bins.reduce((sum, bin) => sum + bin.count, 0));
  const speedBinTotals = speedBins.map((_, binIndex) =>
    sectors.reduce((sum, bins) => sum + (bins[binIndex]?.count ?? 0), 0),
  );
  return {
    sectors,
    totals,
    maxTotal: Math.max(...totals, 0),
    maxSpeed,
    speedBins,
    speedBinTotals,
  };
}

function formatDirectionRange(sectorIndex: number) {
  const sectorSize = 360 / SECTOR_COUNT;
  const start = (sectorIndex * sectorSize - sectorSize / 2 + 360) % 360;
  const end = (start + sectorSize) % 360;
  return `${Math.round(start)}-${Math.round(end)} deg`;
}

function directionCompassLabel(sectorIndex: number) {
  const labels = ["N", "NNE", "ENE", "E", "ESE", "SSE", "S", "SSW", "WSW", "W", "WNW", "NNW"];
  return labels[sectorIndex] ?? `Sector ${sectorIndex + 1}`;
}

export type LiveWindRoseTileProps = {
  speedChannel: DeviceChannelDto;
  directionChannel: DeviceChannelDto;
  rawRows: TelemetryDto[];
  aggregatePoints: TelemetryAggregatePointDto[];
  updatedAt: string | null;
};

export function LiveWindRoseTile({
  speedChannel,
  directionChannel,
  rawRows,
  aggregatePoints,
  updatedAt,
}: LiveWindRoseTileProps) {
  const [activeSegment, setActiveSegment] = useState<ActiveSegment | null>(null);

  const samples = useMemo(
    () => buildWindRoseSamples(speedChannel.key, directionChannel.key, rawRows, aggregatePoints),
    [aggregatePoints, directionChannel.key, rawRows, speedChannel.key],
  );
  const latestSample = samples[samples.length - 1] ?? null;
  const { sectors, totals, maxTotal, speedBins, speedBinTotals } = useMemo(() => buildSectorMatrix(samples), [samples]);
  const totalSamples = samples.length;
  const chartCoreSize = 340;
  const chartPaddingLeft = 42;
  const chartPaddingRight = 18;
  const chartSize = chartCoreSize + chartPaddingLeft + chartPaddingRight;
  const center = chartPaddingLeft + chartCoreSize / 2;
  const innerRadius = 26;
  const outerRadius = 132;
  const ringCount = 5;
  const ringStep = (outerRadius - innerRadius) / ringCount;
  const sectorSize = 360 / SECTOR_COUNT;
  const averageSpeed =
    samples.length > 0 ? samples.reduce((sum, sample) => sum + sample.speed, 0) / samples.length : null;
  const ringRadii = Array.from({ length: ringCount }, (_, index) => innerRadius + ringStep * (index + 1));

  return (
    <Card className="p-[var(--density-panel-padding)]">
      <div className="flex flex-wrap items-start justify-between gap-ds-4">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-ds-2 rounded-full border border-primary/15 bg-primary/10 px-ds-3 py-1 text-micro font-bold uppercase tracking-[0.14em] text-primary">
            <Compass className="h-3.5 w-3.5" aria-hidden />
            Wind rose
          </div>
          <div>
            <h3 className="text-title font-semibold text-foreground">Wind direction distribution</h3>
            <p className="mt-1 text-caption text-muted">
              Speed from {getChannelDisplayLabel(speedChannel)} and direction from {getChannelDisplayLabel(directionChannel)}.
            </p>
          </div>
        </div>

        <div className="grid min-w-56 gap-ds-2 sm:grid-cols-2">
          <div className="rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2">
            <p className="text-micro uppercase tracking-[0.12em] text-muted">Latest wind</p>
            <p className="mt-1 text-title font-semibold text-foreground">
              {latestSample ? `${formatTelemetryValue(latestSample.speed)} ${speedChannel.unit ?? ""}`.trim() : "--"}
            </p>
            <p className="mt-1 text-caption text-muted">
              {latestSample ? `${Math.round(latestSample.direction)} deg` : "Waiting for telemetry"}
            </p>
          </div>
          <div className="rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2">
            <p className="text-micro uppercase tracking-[0.12em] text-muted">Coverage</p>
            <p className="mt-1 text-title font-semibold text-foreground">{samples.length}</p>
            <p className="mt-1 text-caption text-muted">
              {averageSpeed != null
                ? `Avg speed ${formatTelemetryValue(averageSpeed)} ${speedChannel.unit ?? ""}`.trim()
                : updatedAt
                  ? "No paired speed/direction samples"
                  : "Waiting for telemetry"}
            </p>
          </div>
        </div>
      </div>

      {samples.length === 0 ? (
        <div className="mt-ds-5 flex h-[var(--map-height)] items-center justify-center rounded-control border border-dashed border-card-border text-caption text-muted">
          Waiting for paired wind speed and wind direction samples.
        </div>
      ) : (
        <div className="mt-ds-5 grid gap-ds-5 xl:grid-cols-[minmax(0,1fr)_15rem]">
          <div className="rounded-control border border-card-border bg-surface-secondary p-ds-4">
            <div className="relative">
              <svg
                viewBox={`0 0 ${chartSize} ${chartSize}`}
                className="mx-auto block w-full max-w-2xl"
                role="img"
                aria-label="Wind rose chart"
              >
                {ringRadii.map((radius) => (
                  <g key={radius}>
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      fill="none"
                      stroke="var(--border)"
                      strokeDasharray="3 6"
                      opacity={0.55}
                    />
                  </g>
                ))}

                {Array.from({ length: SECTOR_COUNT }, (_, sectorIndex) => {
                  const angle = -90 + sectorIndex * sectorSize;
                  const lineEnd = polarToCartesian(center, center, outerRadius + 8, angle);
                  return (
                    <line
                      key={sectorIndex}
                      x1={center}
                      y1={center}
                      x2={lineEnd.x}
                      y2={lineEnd.y}
                      stroke="var(--border)"
                      strokeDasharray="4 8"
                      opacity={0.35}
                    />
                  );
                })}

                {CARDINAL_LABELS.map(({ label, angle }) => {
                  const point = polarToCartesian(center, center, outerRadius + 20, angle);
                  return (
                    <text
                      key={label}
                      x={point.x}
                      y={point.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-muted text-micro font-semibold"
                    >
                      {label}
                    </text>
                  );
                })}

                <circle cx={center} cy={center} r={innerRadius - 4} fill="var(--bg-surface)" opacity={0.96} />
                <text
                  x={center}
                  y={center}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-muted text-micro font-semibold"
                >
                  calm
                </text>

                {sectors.map((bins, sectorIndex) => {
                  const total = totals[sectorIndex] ?? 0;
                  if (total <= 0 || maxTotal <= 0) {
                    return null;
                  }

                  const startAngle = -90 + sectorIndex * sectorSize - sectorSize / 2 + 2;
                  const endAngle = startAngle + sectorSize - 4;
                  let runningRadius = innerRadius;

                  return bins.map((bin, binIndex) => {
                    if (bin.count <= 0) {
                      return null;
                    }

                    const segmentLength = ((outerRadius - innerRadius) * bin.count) / maxTotal;
                    const nextRadius = runningRadius + segmentLength;
                    const path = describeSectorPath(center, center, runningRadius, nextRadius, startAngle, endAngle);
                    const speedBin = speedBins[binIndex]!;
                    const totalPercent = totalSamples > 0 ? (bin.count / totalSamples) * 100 : 0;
                    const averageSegmentSpeed = bin.count > 0 ? bin.speedTotal / bin.count : 0;
                    const directionLabel = `${directionCompassLabel(sectorIndex)} (${formatDirectionRange(sectorIndex)})`;
                    const speedLabel = `${formatTelemetryValue(speedBin.min)} - ${formatTelemetryValue(speedBin.max)} ${speedChannel.unit ?? ""}`.trim();
                    const isActive = activeSegment?.sectorIndex === sectorIndex && activeSegment?.binIndex === binIndex;

                    runningRadius = nextRadius;

                    return (
                      <path
                        key={`${sectorIndex}-${binIndex}`}
                        d={path}
                        fill={speedBin.color}
                        stroke={isActive ? "var(--foreground)" : "var(--surface-primary)"}
                        strokeWidth={isActive ? "1.5" : "1"}
                        opacity={isActive ? 1 : 0.96}
                        className="cursor-pointer outline-none transition-opacity"
                        tabIndex={0}
                        onMouseEnter={() =>
                          setActiveSegment({
                            sectorIndex,
                            binIndex,
                            count: bin.count,
                            totalPercent,
                            averageSpeed: averageSegmentSpeed,
                            directionLabel,
                            speedLabel,
                          })
                        }
                        onMouseLeave={() => setActiveSegment((current) =>
                          current?.sectorIndex === sectorIndex && current?.binIndex === binIndex ? null : current,
                        )}
                        onFocus={() =>
                          setActiveSegment({
                            sectorIndex,
                            binIndex,
                            count: bin.count,
                            totalPercent,
                            averageSpeed: averageSegmentSpeed,
                            directionLabel,
                            speedLabel,
                          })
                        }
                        onBlur={() => setActiveSegment((current) =>
                          current?.sectorIndex === sectorIndex && current?.binIndex === binIndex ? null : current,
                        )}
                      >
                        <title>
                          {`${directionLabel} | ${speedLabel} | ${bin.count} sample${bin.count === 1 ? "" : "s"} | ${totalPercent.toFixed(1)}%`}
                        </title>
                      </path>
                    );
                  });
                })}
              </svg>

              {activeSegment ? (
                <div className="pointer-events-none absolute right-ds-3 top-ds-3 max-w-[15rem] rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-2 shadow-card">
                  <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Segment</p>
                  <p className="mt-1 text-caption font-semibold text-foreground">{activeSegment.directionLabel}</p>
                  <p className="mt-1 text-caption text-muted">{activeSegment.speedLabel}</p>
                  <div className="mt-ds-2 grid grid-cols-2 gap-x-ds-3 gap-y-1 text-caption">
                    <span className="text-muted">Samples</span>
                    <span className="text-right font-semibold text-foreground">{activeSegment.count}</span>
                    <span className="text-muted">Share</span>
                    <span className="text-right font-semibold text-foreground">{activeSegment.totalPercent.toFixed(1)}%</span>
                    <span className="text-muted">Avg speed</span>
                    <span className="text-right font-semibold text-foreground">
                      {formatTelemetryValue(activeSegment.averageSpeed)} {speedChannel.unit ?? ""}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-ds-3">
            <div className="rounded-control border border-card-border bg-surface-secondary p-ds-4">
              <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Speed bins</p>
              <div className="mt-ds-3 space-y-ds-2">
                {speedBins.map((bin, index) => (
                  <div key={index} className="flex items-center justify-between gap-ds-3 text-caption">
                    <span className="inline-flex items-center gap-ds-2 text-foreground">
                      <span className="h-3 w-3 rounded-full border border-border/60 dark:border-white/20" style={{ backgroundColor: bin.color }} />
                      <span>
                        {formatTelemetryValue(bin.min)} - {formatTelemetryValue(bin.max)}
                      </span>
                    </span>
                    <span className="text-right text-muted">
                      <span className="block">{speedChannel.unit ?? "speed"}</span>
                      <span className="block text-micro">
                        {totalSamples > 0 ? `${(((speedBinTotals[index] ?? 0) / totalSamples) * 100).toFixed(1)}%` : "0.0%"}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-control border border-card-border bg-surface-secondary p-ds-4">
              <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Read me</p>
              <p className="mt-ds-2 text-caption leading-relaxed text-muted">
                Petal length shows how often wind arrived from that direction. Stacked color bands show the speed mix within each direction band.
              </p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
